import { DateTime } from 'luxon';

import SHARED_CONFIG from 'configs/shared';

function pmt(rate, nper, pv) {
    var pvif = Math.pow(1 + rate, -nper);
    var pmt = (pv * rate) / (1 - pvif);

    return pmt;
}

function getInterestCharge(loanType, country, amount, numPayments, daysInMonth) {
    var monthlyInterestRate = getMonthlyInterestRate(loanType, country, amount, numPayments);
    var dailyInterestRate = getDailyInterestRate(monthlyInterestRate);

    if (amount <= 0) {
        return 0;
    }

    let interestCharge = amount * daysInMonth * dailyInterestRate;

    if (interestCharge > monthlyInterestRate * amount) {
        return monthlyInterestRate * amount;
    } else {
        return interestCharge;
    }
}

function getAdditionalSMEFees(loanType, country, amount, numPayments) {
    let additionalFees = 0;

    if (loanType == SHARED_CONFIG.LOAN_TYPES.BUSINESS) {
        if (country.key == SHARED_CONFIG.COUNTRIES.GD.key) {
            switch (numPayments) {
                case 3:
                    additionalFees = amount * 0.095;
                    break;
                case 6:
                    additionalFees = amount * 0.14;
                    break;
                case 9:
                    additionalFees = amount * 0.185;
                    break;
                case 12:
                    additionalFees = amount * 0.23;
                    break;
                case 15:
                    additionalFees = amount * 0.275;
                    break;
                case 18:
                    additionalFees = amount * 0.32;
                    break;
                case 24:
                    additionalFees = amount * 0.41;
                    break;
                case 30:
                    additionalFees = amount * 0.5;
                    break;
                case 36:
                    additionalFees = amount * 0.59;
                    break;
                case 48:
                    additionalFees = amount * 0.77;
                    break;
                case 60:
                    additionalFees = amount * 0.95;
                    break;
                default:
                    break;
            }
        }
    }

    return additionalFees;
}

function getMonthlyInterestRate(loanType, country, amount, numPayments, metadata = {}) {
    switch (loanType) {
        case SHARED_CONFIG.LOAN_TYPES.CAR:
            if (numPayments < 6) {
                return 0.04;
            } else {
                if (metadata?.carLoanInsuranceType == 'Full Comprehensive') {
                    if (amount <= 50000) {
                        return 0.033;
                    } else {
                        return 0.028;
                    }
                } else {
                    if (amount <= 50000) {
                        return 0.035;
                    } else {
                        return 0.03;
                    }
                }
            }
        case SHARED_CONFIG.LOAN_TYPES.BUSINESS:
            switch (country.key) {
                case SHARED_CONFIG.COUNTRIES.TT.key:
                    if (numPayments < 6) {
                        return 0.04;
                    } else {
                        if (amount <= 50000) {
                            return 0.035;
                        } else {
                            return 0.03;
                        }
                    }
                case SHARED_CONFIG.COUNTRIES.BB.key:
                    if (numPayments < 6) {
                        return 0.04;
                    } else {
                        if (amount <= 15000) {
                            return 0.035;
                        } else if (amount >= 25000) {
                            return 0.03;
                        }
                    }
                case SHARED_CONFIG.COUNTRIES.GD.key:
                    if (metadata?.isExistingCustomer) {
                        return 0.01;
                    } else {
                        return 0.0125;
                    }
            }
        case SHARED_CONFIG.LOAN_TYPES.PERSONAL:
            switch (country.key) {
                case SHARED_CONFIG.COUNTRIES.GD.key:
                    return 0.06;
                case SHARED_CONFIG.COUNTRIES.AG.key:
                    return 0.05;
                default:
                    return 0.06;
            }
    }
}

function getDailyInterestRate(monthlyInterestRate) {
    return (monthlyInterestRate * 12) / 365;
}

export function calculate(loanType, country, amount, numPayments, metadata = {}) {
    let currentDate = DateTime.now(),
        daysLeftInCurrentMonth = currentDate.daysInMonth - currentDate.day,
        interestChargeForCurrentMonth = getInterestCharge(
            loanType,
            country,
            amount,
            numPayments,
            daysLeftInCurrentMonth,
        );

    var monthlyPayments = [
        {
            id: 0,
            date: currentDate.toLocaleString({ month: 'short', year: 'numeric' }),
            payment: null,
        },
    ];

    // Currently used for SME, but we can extend this for Personal and Car loans
    let additionalFees = getAdditionalSMEFees(loanType, country, amount, numPayments);

    var outstandingPrincipal = amount,
        totalMonthlyPayments = 0;

    for (var i = 1; i <= numPayments; i++) {
        let date = DateTime.now().plus({ months: i }).startOf('month'),
            daysLeftInMonth = date.daysInMonth;

        // Calculate Interest
        let interestCharge = getInterestCharge(loanType, country, outstandingPrincipal, numPayments, daysLeftInMonth);

        // Calculate the Monthly Payment
        let presentValue = parseFloat(amount) + parseFloat(interestChargeForCurrentMonth);
        let monthlyPayment = pmt(
            getMonthlyInterestRate(loanType, country, amount, numPayments, metadata),
            numPayments,
            presentValue,
        );

        // Calculate the Principal Repayment
        let principalRepayment;
        if (i == 1) {
            principalRepayment = monthlyPayment - interestCharge - interestChargeForCurrentMonth;
        } else {
            principalRepayment = monthlyPayment - interestCharge;
        }

        // Calculate the new Outstanding Principal
        outstandingPrincipal -= principalRepayment;

        // Calculate the new Total Interest
        totalMonthlyPayments += monthlyPayment;

        if (i <= 6) {
            monthlyPayments.push({
                id: i,
                date: `${date.toLocaleString({ month: 'short', year: 'numeric' })} ${i == 6 ? 'and onwards' : ''}`,
                payment: parseFloat(monthlyPayment.toFixed(2)) + additionalFees / parseInt(numPayments),
            });
        }
    }

    var totalInterestCharge = totalMonthlyPayments - amount,
        effectiveInterestRate = totalInterestCharge / amount;

    return {
        amount: amount,
        monthlyPayments: monthlyPayments,
        totalInterestCharge: totalInterestCharge + additionalFees,
        effectiveInterestRate: effectiveInterestRate,
    };
}

export default {
    calculate,
};
